// Curated stamp icons for the loyalty stamp card. A venue admin picks one in
// Loyalty → Home stamp card; it renders inside each filled stamp on the app
// home page (and in the design preview). Inline SVG (currentColor) so it tints
// with whatever colour the parent sets, matching how icons are drawn
// elsewhere in the SPA (e.g. venue.tsx, rewards.tsx).
import type { CSSProperties, ReactNode } from "react";

export const STAMP_ICONS = ["check", "star", "heart", "coffee"] as const;
export type StampIcon = (typeof STAMP_ICONS)[number];

export const STAMP_ICON_LABELS: Record<StampIcon, string> = {
  check: "Check",
  star: "Star",
  heart: "Heart",
  coffee: "Coffee cup",
};

const PATHS: Record<StampIcon, ReactNode> = {
  check: (
    <path
      fillRule="evenodd"
      d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
      clipRule="evenodd"
    />
  ),
  star: (
    <path
      fillRule="evenodd"
      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
      clipRule="evenodd"
    />
  ),
  heart: (
    <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" />
  ),
  coffee: (
    <path
      fillRule="evenodd"
      d="M4 6.75A.75.75 0 0 1 4.75 6h9.5a.75.75 0 0 1 .75.75V8h2.5a2.5 2.5 0 0 1 0 5H15a5 5 0 0 1-10 0V6.75Zm11 2.75v2h1.5a1 1 0 0 0 0-2H15ZM3.75 18.5a.75.75 0 0 0 0 1.5h12a.75.75 0 0 0 0-1.5h-12Z"
      clipRule="evenodd"
    />
  ),
};

export function isStampIcon(v: unknown): v is StampIcon {
  return typeof v === "string" && (STAMP_ICONS as readonly string[]).includes(v);
}

export function StampGlyph({
  icon,
  className,
  style,
}: {
  icon: StampIcon;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      style={style}
      aria-hidden
    >
      {PATHS[icon]}
    </svg>
  );
}
